import { useForm, Controller, SubmitHandler } from "react-hook-form";
import CustomReactSelector from "components/common/Selector/CustomReactSelector";
import { toggleInvalidInput } from "core/helpers";
import { FC, useEffect, useState } from "react";
import { API } from "constants/RestApi";
import useBaseAction from "hooks/core/useBaseActions";
import { toast } from "react-toastify";
import { IBaseModalProps } from "core/types/elements/IModal";
import { IFeatureData, ISharedConfig } from "components/features/types";
import BaseModal from "components/common/BaseModal/BaseModal";
import "./AddEditConfigModal.scss";

interface IProps extends IBaseModalProps<IFeatureData> {
  onClose: () => void;
  feature: IFeatureData;
}

interface IFormData {
  isEnabled: boolean;
  isActive: boolean;
  configType: "tenant" | "shared";
  configuration?: { value: string; label: string };
}

const AddEditModalConfig: FC<IProps> = ({ onClose, showModal, onSave, feature }) => {
  const { handleSubmit, control, setValue, watch } = useForm<IFormData>({
    defaultValues: {
      isEnabled: false,
      isActive: false,
      configType: "tenant",
    },
  });

  const [configurations, setConfigurations] = useState<ISharedConfig[]>([]);
  const [initialConfigType, setInitialConfigType] = useState<"tenant" | "shared">("tenant");
  const { request } = useBaseAction();

  // Watch configType to conditionally show/hide dropdown
  const configType = watch("configType");
  const isActive = watch("isActive");

  const fetchSharedConfigs = async () => {
    const params = {
      filter: `featureId/value eq '${feature.featureId}'`,
    };
    try {
      const response = await request({
        method: "GET",
        url: `${API.ADMIN_SHARED_CONFIGS}`,
        params,
        useLoader: true,
      });
      setConfigurations(response as ISharedConfig[]);
    } catch (error: any) {
      toast.error(error?.message || "Failed to fetch shared configs");
    }
  };

  const modalBody = () => (
    <form className="config-modal">
      <div className="row">
        {/* Always show checkboxes, but disable them when creating new feature */}
        {/* Allow to use feature checkbox */}
        <div className="col-12 mb-3">
          <div className="form-check d-flex align-items-center">
            <Controller
              control={control}
              name="isActive"
              render={({ field: { onChange, value } }) => (
                <input
                  className="form-check-input me-2"
                  type="checkbox"
                  id="isActive"
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
            <label className="form-check-label" htmlFor="isActive">
              Allow to use feature:
            </label>
            <div className="ms-2 help-tooltip">
              <span className="help-icon" title="Allow tenant to use this feature">
                ?
              </span>
              <div className="help-content">
                <strong>Allow to use feature:</strong>
                <br />
                This checkbox determines whether the tenant has permission to access and utilize
                this feature. When checked, the tenant can see and interact with the feature in
                their interface.
              </div>
            </div>
          </div>
        </div>

        {/* Active configuration checkbox */}
        <div className="col-12 mb-3">
          <div className="form-check d-flex align-items-center">
            <Controller
              control={control}
              name="isEnabled"
              render={({ field: { onChange, value } }) => (
                <input
                  className="form-check-input me-2"
                  type="checkbox"
                  id="isEnabled"
                  checked={value}
                  onChange={onChange}
                  disabled={!isActive}
                />
              )}
            />
            <label className="form-check-label" htmlFor="isEnabled">
              Active:
            </label>
            <div className="ms-2 help-tooltip">
              <span className="help-icon" title="Activate this configuration">
                ?
              </span>
              <div className="help-content">
                <strong>Active:</strong>
                <br />
                This checkbox determines whether the configuration is currently active and will be
                applied. When checked, the configuration is active and will be used by the system.
                When unchecked, the configuration is inactive and will not be applied.
              </div>
            </div>
          </div>
        </div>

        {/* Configuration type radio buttons */}
        <div className={`col-12 ${feature.configId ? "configuration-section" : ""}`}>
          <div className="d-flex align-items-center mb-2">
            <label className="form-label fw-bold mb-0" htmlFor="configType">
              Configuration Type
            </label>
            <div className="ms-2 help-tooltip">
              <span className="help-icon" title="Configuration type selection">
                ?
              </span>
              <div className="help-content">
                <strong>Configuration Type:</strong>
                <br />
                Choose how this feature will be configured:
                <br />• <strong>Tenant Admin:</strong> The tenant's administrator will configure
                this feature locally
                <br />• <strong>Shared:</strong> Use a pre-configured shared configuration from the
                system
              </div>
            </div>
          </div>
          <div className="radio-group">
            <div className="form-check">
              <Controller
                control={control}
                name="configType"
                render={({ field: { onChange, value } }) => (
                  <input
                    className="form-check-input"
                    type="radio"
                    id="configTypeTenant"
                    value="tenant"
                    checked={value === "tenant"}
                    onChange={onChange}
                  />
                )}
              />
              <label className="form-check-label" htmlFor="configTypeTenant">
                Configured by Tenant Admin
              </label>
            </div>
            <div className="form-check">
              <Controller
                control={control}
                name="configType"
                render={({ field: { onChange, value } }) => (
                  <input
                    className="form-check-input"
                    type="radio"
                    id="configTypeShared"
                    value="shared"
                    checked={value === "shared"}
                    onChange={onChange}
                    disabled={
                      !feature.capabilities?.includes("CanUseSharedConfigurations") || !isActive
                    }
                  />
                )}
              />
              <label className="form-check-label" htmlFor="configTypeShared">
                Shared configuration
              </label>
            </div>
          </div>
          {!feature.capabilities?.includes("CanUseSharedConfigurations") && (
            <small className="text-muted">
              This feature does not support shared configurations. Only tenant admin configuration
              is available.
            </small>
          )}
        </div>

        {/* Configuration dropdown - only show when shared is selected and feature supports it */}
        {configType === "shared" && feature.capabilities?.includes("CanUseSharedConfigurations") && (
          <div className="col-12 mb-3 mt-3">
            <div className="d-flex align-items-center mb-2">
              <label htmlFor="configuration" className="form-label mb-0">
                Configuration
              </label>
              <div className="ms-2 help-tooltip">
                <span className="help-icon" title="Select shared configuration">
                  ?
                </span>
                <div className="help-content">
                  <strong>Configuration:</strong>
                  <br />
                  Select a pre-configured shared configuration that will be applied to this feature.
                  This configuration contains all the necessary settings and parameters for the
                  feature to work properly.
                </div>
              </div>
            </div>
            <Controller
              control={control}
              name="configuration"
              rules={{ required: configType === "shared" }}
              render={({ field: { onChange, value }, fieldState }) => (
                <CustomReactSelector
                  className={`${toggleInvalidInput(fieldState.invalid)}`}
                  value={value}
                  onChange={onChange}
                  placeholder="Select configuration"
                  options={configurations
                    ?.filter(conf => conf?.isEnabled)
                    .map(({ name, id }) => ({ value: id, label: name }))}
                  closeMenuOnSelect
                  hideSelectedOptions
                  isClearable
                />
              )}
            />
          </div>
        )}
      </div>
    </form>
  );

  const onSubmit: SubmitHandler<IFormData> = async formData => {
    if (onSave) {
      // Transform form data to match expected format
      const transformedData = {
        ...feature,
        isEnabled: formData.isEnabled,
        isActive: formData.isActive,
        configType: formData.configType,
        configuration: formData.configuration,
        // Add flag to indicate if config type changed
        configTypeChanged: feature.configId && initialConfigType !== formData.configType,
        previousConfigType: initialConfigType,
      };
      onSave(transformedData, !!feature?.id);
    }
  };

  useEffect(() => {
    if (feature.featureId) {
      fetchSharedConfigs();
    }

    // Check if feature supports shared configurations
    const supportsSharedConfig = feature.capabilities?.includes("CanUseSharedConfigurations");
    const type = feature?.configuration?.type === "Tenant" ? "tenant" : "shared";
    // Set initial values if editing
    if (feature.configId && supportsSharedConfig) {
      setValue(
        "configuration",
        feature.configId && feature?.configName
          ? { value: feature.configId, label: feature?.configName || "" }
          : undefined,
      );
    } else {
      // If no shared config support or no config ID, force tenant admin
      setValue("configuration", undefined);
    }

    // Set default values based on existing feature state
    if (feature.configId) {
      setValue("isEnabled", feature?.isEnabled ?? false);
      setValue("isActive", feature?.configuration ?? false);
    }

    if (feature.configId) {
      setValue("configType", type);
      setInitialConfigType(type);
    } else {
      setValue("configType", "tenant");
      setInitialConfigType("tenant");
    }

    return () => {
      setValue("configuration", undefined);
      setValue("isEnabled", false);
      setValue("isActive", false);
      setValue("configType", "tenant");
    };
  }, [feature.featureId, feature.configId, feature.id, feature.capabilities, setValue]);

  return (
    <BaseModal
      modalTitle={
        feature.configId
          ? `Edit ${feature?.name} Configuration`
          : `Create ${feature?.name} Configuration`
      }
      onClose={onClose}
      onCancel={onClose}
      showModal={showModal}
      modalBody={modalBody()}
      onSave={handleSubmit(onSubmit)}
      cancelActionBtnText="Close"
      successBtnText="Save"
      disableCloseActions
      isDisabledSubmit={!feature.configId && !isActive}
    />
  );
};

export default AddEditModalConfig;
